import { useState } from 'react';
import { useTranslate } from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';
import { Box, Typography, TextField, IconButton, Tooltip, MenuItem, Popover, List, ListItemButton, ListItemText } from '@mui/material';
import AlertInfoIcon from '@/assets/icons/alert-info-icon.svg?react';
import CreateIcon from '@/assets/icons/create-icon.svg?react';
import DeleteSmallIcon from '@/assets/icons/delete-small-icon.svg?react';
import CriteriaInput from './CriteriaInput.jsx';
import { Palette } from '@/themes/themeOverrides/Palette.js';
import { criteriaConfig, criteriaOptions, getInitialValueFor } from '@/constants/CampaignCriteria.js';
import { getAvailableGroupOptions, getAvailableInGroupOptions } from '@/helpers/marketing/campaignCriteriaOptions.js';

const getGroupKey = group => group.id ?? `${group.baseOption}-${JSON.stringify(group.baseValue ?? '')}`;
const getRowKey = row => row.id ?? `${row.operator}-${row.option}-${JSON.stringify(row.value ?? '')}`;
const getSubgroupKey = subgroup => subgroup.id ?? subgroup.rows?.map(getRowKey).join('|');

const CriteriaStep = () => {
    const translate = useTranslate();
    const { setValue } = useFormContext();
    const watchedCriteria = useWatch({ name: 'criteria' });
    const OPTION_KEYS = criteriaOptions;

    const groups = Array.isArray(watchedCriteria) ? watchedCriteria : [];

    const [anchorAddGroupEl, setAnchorAddGroupEl] = useState(null);

    const openAddGroupPopover = event => {
        setAnchorAddGroupEl(event.currentTarget);
    };

    const closeAddGroupPopover = () => {
        setAnchorAddGroupEl(null);
    };

    const handleSelectGroupCriterion = optionKey => {
        const next = [...groups, { id: crypto.randomUUID(), baseOption: optionKey, baseValue: getInitialValueFor(optionKey), subgroups: [] }];
        setValue('criteria', next, { shouldDirty: true });
        closeAddGroupPopover();
    };

    const handleBaseValueChange = (groupIndex, value) => {
        const next = [...groups];
        next[groupIndex] = { ...next[groupIndex], baseValue: value };
        setValue('criteria', next, { shouldDirty: true });
    };

    const [anchorAddInGroupEl, setAnchorAddInGroupEl] = useState(null);
    const [activeGroupForAdd, setActiveGroupForAdd] = useState(null);
    const [activeSubgroupForAdd, setActiveSubgroupForAdd] = useState(null);

    const openAddInGroupPopover = (event, groupIndex, subgroupIndex = null) => {
        setActiveGroupForAdd(groupIndex);
        setActiveSubgroupForAdd(subgroupIndex);
        setAnchorElFor(event.currentTarget);
    };

    const setAnchorElFor = el => setAnchorAddInGroupEl(el);

    const closeAddInGroupPopover = () => {
        setAnchorAddInGroupEl(null);
        setActiveGroupForAdd(null);
        setActiveSubgroupForAdd(null);
    };

    const handleSelectInGroupCriterion = optionKey => {
        if (activeGroupForAdd === null || activeGroupForAdd === undefined) return;
        const next = [...groups];
        const group = next[activeGroupForAdd];
        const subgroups = group?.subgroups ? [...group.subgroups] : [];

        if (activeSubgroupForAdd === null || activeSubgroupForAdd === -1 || subgroups[activeSubgroupForAdd] === undefined) {
            const initialValue = getInitialValueFor(optionKey);
            const newSubgroup = { id: crypto.randomUUID(), rows: [{ id: crypto.randomUUID(), operator: 'oraz', option: optionKey, value: initialValue }] };
            subgroups.push(newSubgroup);
        } else {
            const sg = subgroups[activeSubgroupForAdd];
            const rows = sg.rows ? [...sg.rows] : [];
            const initialValue = getInitialValueFor(optionKey);
            rows.push({ id: crypto.randomUUID(), operator: 'oraz', option: optionKey, value: initialValue });
            subgroups[activeSubgroupForAdd] = { ...sg, rows };
        }

        next[activeGroupForAdd] = { ...group, subgroups };
        setValue('criteria', next, { shouldDirty: true });
        closeAddInGroupPopover();
    };

    const handleCriteriaChange = (groupIndex, subgroupIndex, index, value) => {
        const next = [...groups];
        const group = next[groupIndex];
        const subgroups = [...group.subgroups];
        const sg = subgroups[subgroupIndex];
        const rows = [...sg.rows];
        rows[index] = { ...rows[index], value };
        subgroups[subgroupIndex] = { ...sg, rows };
        next[groupIndex] = { ...group, subgroups };
        setValue('criteria', next, { shouldDirty: true });
    };

    const handleOperatorChange = (groupIndex, subgroupIndex, index, operator) => {
        const next = [...groups];
        const group = next[groupIndex];
        const subgroups = [...group.subgroups];
        const sg = subgroups[subgroupIndex];
        const rows = [...sg.rows];
        rows[index] = { ...rows[index], operator };
        subgroups[subgroupIndex] = { ...sg, rows };
        next[groupIndex] = { ...group, subgroups };
        setValue('criteria', next, { shouldDirty: true });
    };

    const handleRemoveCriteria = (groupIndex, subgroupIndex, indexToRemove) => {
        const next = [...groups];
        const group = next[groupIndex];
        const subgroups = [...group.subgroups];
        const sg = subgroups[subgroupIndex];
        const rows = sg.rows.filter((_, idx) => idx !== indexToRemove);
        subgroups[subgroupIndex] = { ...sg, rows };
        next[groupIndex] = { ...group, subgroups };
        setValue('criteria', next, { shouldDirty: true });
    };

    const handleRemoveGroup = groupIndex => {
        const next = groups.filter((_, idx) => idx !== groupIndex);
        setValue('criteria', next, { shouldDirty: true });
    };

    return (
        <Box display='flex' flexDirection='column' gap='48px' maxWidth='640px'>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '20px',
                    borderRadius: '16px',
                    backgroundColor: Palette.blue.blue50,
                    '& svg': {
                        flexShrink: 0,
                    },
                }}
            >
                <AlertInfoIcon />
                <Box className='message-box'>
                    <Typography sx={{ fontSize: '14px', fontWeight: '400', lineHeight: '20px' }}>
                        {translate('resources.campaign.labels.criteriaInfo')}
                    </Typography>
                </Box>
            </Box>
            <Box>
                <Typography sx={{ fontSize: '14px', fontWeight: '400', lineHeight: '20px', marginBottom: '16px' }}>
                    {translate('resources.campaign.labels.criteriaSectionTitle')}
                </Typography>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '4px',
                        '& .MuiFormLabel-root.MuiInputLabel-root.MuiInputLabel-shrink, & .MuiFormLabel-root.MuiInputLabel-root.MuiFormLabel-filled, & .MuiFormLabel-root.MuiInputLabel-root.MuiFormLabel-focused':
                            {
                                backgroundColor: Palette.gray.gray75,
                                borderRadius: '4px',
                            },
                    }}
                >
                    {groups.map((group, groupIndex) => (
                        <Box
                            key={getGroupKey(group)}
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '12px',
                                '& .MuiTextField-root': {
                                    margin: '0px',
                                },
                                '& .MuiAutocomplete-root': {
                                    minWidth: '248px',
                                },
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    padding: '20px',
                                    borderRadius: '16px',
                                    backgroundColor: Palette.gray.gray75,
                                }}
                            >
                                <Typography sx={{ minWidth: '88px', fontSize: '14px', color: Palette.gray.gray600 }}>
                                    {translate('resources.campaign.labels.when')}
                                </Typography>

                                <CriteriaInput
                                    option={group.baseOption}
                                    value={group.baseValue}
                                    onChange={value => handleBaseValueChange(groupIndex, value)}
                                    label={undefined}
                                    productSource={`criteria_base_${groupIndex}`}
                                    categorySource={`criteria_base_categories_${groupIndex}`}
                                />

                                <Box sx={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }} />
                                <Tooltip
                                    placement='bottom'
                                    title={translate('resources.campaign.labels.removeGroup')}
                                    arrow
                                    classes={{ tooltip: 'tooltip-black' }}
                                >
                                    <IconButton
                                        color='primary'
                                        onClick={() => handleRemoveGroup(groupIndex)}
                                        aria-label={translate('resources.campaign.labels.removeGroupAria')}
                                    >
                                        <DeleteSmallIcon />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip
                                    placement='bottom'
                                    title={translate('resources.campaign.labels.addCriterionGroup')}
                                    arrow
                                    classes={{ tooltip: 'tooltip-black' }}
                                >
                                    <IconButton
                                        color='primary'
                                        onClick={e => openAddInGroupPopover(e, groupIndex, -1)}
                                        aria-label={translate('resources.campaign.labels.addCriterionGroup')}
                                    >
                                        <CreateIcon />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                            <Box display='flex' flexDirection='column' gap='8px'>
                                {group.subgroups?.map((subgroup, subgroupIndex) => (
                                    <Box
                                        key={getSubgroupKey(subgroup)}
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            borderRadius: '16px',
                                            backgroundColor: Palette.gray.gray75,
                                        }}
                                    >
                                        {subgroup.rows?.map((row, index) => (
                                            <Box
                                                className='criteria-row'
                                                key={getRowKey(row)}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '12px',
                                                    padding: '20px',
                                                    '& + .criteria-row': {
                                                        paddingTop: '0px',
                                                    },
                                                }}
                                            >
                                                <TextField
                                                    select
                                                    value={row.operator}
                                                    onChange={e => handleOperatorChange(groupIndex, subgroupIndex, index, e.target.value)}
                                                    size='small'
                                                    sx={{ minWidth: '88px' }}
                                                >
                                                    <MenuItem value='oraz'>{translate('campaign.criteria.operator.and')}</MenuItem>
                                                    <MenuItem value='lub'>{translate('campaign.criteria.operator.or')}</MenuItem>
                                                </TextField>
                                                <CriteriaInput
                                                    option={row.option}
                                                    value={row.value}
                                                    onChange={value => handleCriteriaChange(groupIndex, subgroupIndex, index, value)}
                                                    size='small'
                                                    label={undefined}
                                                    productSource={`criteria_${groupIndex}_${subgroupIndex}_${index}`}
                                                    categorySource={`criteria_categories_${groupIndex}_${subgroupIndex}_${index}`}
                                                />
                                                <Box sx={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }} />
                                                <Tooltip
                                                    placement='bottom'
                                                    title={translate('resources.campaign.labels.removeCriterion')}
                                                    arrow
                                                    classes={{ tooltip: 'tooltip-black' }}
                                                >
                                                    <IconButton
                                                        color='primary'
                                                        onClick={() => handleRemoveCriteria(groupIndex, subgroupIndex, index)}
                                                        aria-label={translate('resources.campaign.labels.removeCriterion')}
                                                    >
                                                        <DeleteSmallIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip
                                                    placement='bottom'
                                                    title={translate('resources.campaign.labels.addCriterion')}
                                                    arrow
                                                    classes={{ tooltip: 'tooltip-black' }}
                                                >
                                                    <IconButton
                                                        color='primary'
                                                        onClick={e => openAddInGroupPopover(e, groupIndex, subgroupIndex)}
                                                        aria-label={translate('resources.campaign.labels.addCriterion')}
                                                    >
                                                        <CreateIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        ))}
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    ))}
                    <Box sx={{ borderRadius: '12px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                padding: '24px 20px',
                                borderRadius: '16px',
                                backgroundColor: Palette.gray.gray75,
                            }}
                        >
                            <Tooltip
                                placement='bottom'
                                title={translate('resources.campaign.labels.addCriterionGroup')}
                                arrow
                                classes={{ tooltip: 'tooltip-black' }}
                            >
                                <IconButton color='primary' onClick={openAddGroupPopover} aria-label={translate('resources.campaign.labels.addCriterionGroup')}>
                                    <CreateIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>
                    <Popover
                        open={Boolean(anchorAddGroupEl)}
                        anchorEl={anchorAddGroupEl}
                        onClose={closeAddGroupPopover}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    >
                        <Box sx={{ padding: '12px !important' }}>
                            <Typography
                                sx={{
                                    fontSize: '10px',
                                    fontWeight: 700,
                                    padding: '8px 16px 12px',
                                }}
                            >
                                {translate('resources.campaign.labels.addCriterionHeader')}
                            </Typography>
                            <List
                                sx={{
                                    boxShadow: 'none !important',
                                    '& .MuiButtonBase-root.MuiListItemButton-root': {
                                        padding: '12px 16px',
                                        borderRadius: '8px',
                                        '&:hover': {
                                            backgroundColor: Palette.yellow.yellow50,
                                        },
                                        '& .MuiListItemText-root': {
                                            margin: '0px',
                                        },
                                    },
                                    '& .MuiTypography-root': {
                                        fontSize: '14px',
                                    },
                                }}
                            >
                                {getAvailableGroupOptions(groups, OPTION_KEYS).map(optionKey => (
                                    <ListItemButton key={optionKey} onClick={() => handleSelectGroupCriterion(optionKey)}>
                                        <ListItemText primary={translate(criteriaConfig[optionKey].label)} />
                                    </ListItemButton>
                                ))}
                            </List>
                        </Box>
                    </Popover>

                    <Popover
                        open={Boolean(anchorAddInGroupEl)}
                        anchorEl={anchorAddInGroupEl}
                        onClose={closeAddInGroupPopover}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    >
                        <Box sx={{ padding: '12px !important' }}>
                            <Typography
                                sx={{
                                    fontSize: '10px',
                                    fontWeight: 700,
                                    padding: '8px 16px 12px',
                                }}
                            >
                                {translate('resources.campaign.labels.addCriterionHeader')}
                            </Typography>
                            <List
                                sx={{
                                    boxShadow: 'none !important',
                                    '& .MuiButtonBase-root.MuiListItemButton-root': {
                                        padding: '12px 16px',
                                        borderRadius: '8px',
                                        '&:hover': {
                                            backgroundColor: Palette.yellow.yellow50,
                                        },
                                        '& .MuiListItemText-root': {
                                            margin: '0px',
                                        },
                                    },
                                    '& .MuiTypography-root': {
                                        fontSize: '14px',
                                    },
                                }}
                            >
                                {getAvailableInGroupOptions(groups, OPTION_KEYS, activeGroupForAdd).map(optionKey => (
                                    <ListItemButton key={optionKey} onClick={() => handleSelectInGroupCriterion(optionKey)}>
                                        <ListItemText primary={translate(criteriaConfig[optionKey].label)} />
                                    </ListItemButton>
                                ))}
                            </List>
                        </Box>
                    </Popover>
                </Box>
            </Box>
        </Box>
    );
};

export default CriteriaStep;
